import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, Send } from "lucide-react";
import { postNewsletter } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await postNewsletter({ email });
      toast.success("You're on the list. Welcome to the Institute.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative border-t border-border bg-secondary/40">
      <div className="container-x py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-mono-mini text-muted-foreground mb-4">Newsletter · The Quarterly</div>
            <h3 className="font-display text-3xl md:text-4xl tracking-tight font-medium text-balance">
              Receive essays, research and standards from the Institute.
            </h3>
            <form onSubmit={submit} data-testid="footer-newsletter-form" className="mt-8 flex items-center gap-2 max-w-md">
              <input
                data-testid="footer-newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@institution.org"
                className="flex-1 h-12 px-4 rounded-md border border-border bg-card text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="h-12 px-4 rounded-md bg-foreground text-background inline-flex items-center gap-2 text-sm font-medium hover:opacity-90 disabled:opacity-60"
              >
                {loading ? "..." : <>Subscribe <Send className="h-3.5 w-3.5" /></>}
              </button>
            </form>
            <p className="mt-4 text-xs text-muted-foreground">UK English. No spam. Unsubscribe in one click.</p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-8">
            <FooterCol title="Institute" links={[["About", "/about"], ["Leadership", "/about"], ["Partnerships", "/contact"], ["Press", "/contact"]]} />
            <FooterCol title="Discipline" links={[["The Science", "/science"], ["Research", "/research"], ["Insights", "/insights"], ["Case Studies", "/case-studies"]]} />
            <FooterCol title="Programmes" links={[["Academy", "/training"], ["CleanabilityIQ", "/platform"], ["Sectors", "/sectors"], ["Membership", "/membership"]]} />
            <FooterCol title="Contact" links={[["Enquire", "/contact"], ["Speaking", "/contact"], ["Consultancy", "/contact"], ["Global Map", "/map"]]} />
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-border flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="font-display text-2xl tracking-tight font-medium">The Cleanability Institute</div>
            <p className="text-xs text-muted-foreground mt-2 max-w-md">
              An independent research institute, training academy and standards body for the science of cleanability across the built environment.
            </p>
          </div>
          <div className="font-mono-mini text-muted-foreground">
            © {new Date().getFullYear()} The Cleanability Institute · Registered globally
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="font-mono-mini text-muted-foreground mb-4">{title}</div>
      <ul className="space-y-2.5">
        {links.map(([label, to]) => (
          <li key={label}>
            <Link
              to={to}
              data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-foreground/80 hover:text-[hsl(var(--accent))] inline-flex items-center gap-1 group"
            >
              {label}
              <ArrowUpRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
